// Plan 24c Phase B, Task 5 — Reconnecting Overlay screen (Screen 28)
//
// Full-viewport overlay displayed while the client is attempting to reconnect
// to the game server.  It is NOT dismissible via ESC — the player must stay on
// this screen until the connection either succeeds or fails (the latter
// navigating to 'error-modal' via the caller).
//
// Spinner implementation choice: Option B (CSS border-based arc).
// Rationale: no SVG markup required, fewer DOM nodes, and the border-top-color
// technique is universally understood and trivially testable via data-spinner.
// GSAP rotates the div 360° in 1 second, repeat: -1, ease: 'linear'.
//
// Design tokens (docs/design/tokens.json):
//   typography.family.headline = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif"
//   typography.family.body     = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif"
//   typography.shadow.title    = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)"
//   color.brand.amber          = "#E8A020"
//   color.text.secondary       = "#B0B8C8"
//   color.ui.overlay-dim       = "rgba(0, 0, 0, 0.60)"
//   spacing.lg                 = "24px"
//   z-index.loading            = 600

import type { ScreenHandle, ScreenMountContext } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ReconnectingVariant =
  | 'reconnecting'
  | 'server-update'
  | 'server-unavailable'
  | 'connection-interrupted';

export type ReconnectingPayload = {
  /** Optional variant; default 'reconnecting'. */
  variant?: ReconnectingVariant;
  /** Optional title override; default 'Reconnecting'. */
  title?: string;
  /** Optional subtitle override; default 'Trying to rejoin the game.' */
  subtitle?: string;
};

const VARIANT_COPY: Record<ReconnectingVariant, { title: string; subtitle: string }> = {
  reconnecting: {
    title: 'Reconnecting',
    subtitle: 'Trying to rejoin your game.',
  },
  'server-update': {
    title: 'Server Update',
    subtitle: "Quick update in progress. We'll try to reconnect you to your previous game state.",
  },
  'server-unavailable': {
    title: 'Server Unavailable',
    subtitle: "The game server is down or unreachable. We'll keep trying to reconnect.",
  },
  'connection-interrupted': {
    title: 'Connection Interrupted',
    subtitle: "Check your connection. We'll keep trying to reconnect.",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createReconnectingScreen(
  ctx: ScreenMountContext<ReconnectingPayload>,
): ScreenHandle {
  const { host, gsapRegistry } = ctx;
  const payload = ctx.payload ?? {};
  const variantCopy = VARIANT_COPY[payload.variant ?? 'reconnecting'];

  let disposed = false;
  let spinnerHandle: GsapTimelineHandle | null = null;

  // ── Build root overlay ─────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.setAttribute('data-screen', 'reconnecting');
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '600', // z-index.loading
    background: 'rgba(0,0,0,0.60)', // color.ui.overlay-dim
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px', // spacing.lg
  });

  // ── Logo text ──────────────────────────────────────────────────────────────

  const logo = document.createElement('h3');
  logo.textContent = 'SMASHPARTY';
  Object.assign(logo.style, {
    margin: '0',
    fontFamily: "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif", // typography.family.headline
    fontSize: '28px', // typography.size.h3
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: '1px',
  });
  root.appendChild(logo);

  // ── Title ──────────────────────────────────────────────────────────────────

  const titleEl = document.createElement('h1');
  titleEl.textContent = payload.title ?? variantCopy.title;
  Object.assign(titleEl.style, {
    margin: '0',
    fontFamily: "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif", // typography.family.headline
    fontSize: '48px', // typography.size.h1
    maxWidth: 'min(820px, calc(100vw - 40px))',
    color: '#FFFFFF',
    textAlign: 'center',
    textShadow: '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)', // typography.shadow.title
  });
  root.appendChild(titleEl);

  // ── Spinner (Option B: border-arc div) ─────────────────────────────────────

  const spinner = document.createElement('div');
  spinner.setAttribute('data-spinner', '');
  Object.assign(spinner.style, {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    border: '4px solid rgba(232,160,32,0.20)', // brand.amber at low opacity for track
    borderTopColor: '#E8A020', // color.brand.amber — the moving arc
    flexShrink: '0',
  });
  root.appendChild(spinner);

  // ── Subtitle ───────────────────────────────────────────────────────────────

  const subtitleEl = document.createElement('p');
  subtitleEl.textContent = payload.subtitle ?? variantCopy.subtitle;
  Object.assign(subtitleEl.style, {
    margin: '0',
    fontFamily: "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif", // typography.family.body
    fontSize: '18px',
    lineHeight: '1.35',
    maxWidth: 'min(760px, calc(100vw - 40px))',
    padding: '0 20px',
    color: '#B0B8C8', // color.text.secondary
    textAlign: 'center',
  });
  root.appendChild(subtitleEl);

  // ── Mount root ─────────────────────────────────────────────────────────────

  host.appendChild(root);

  // ── Animate spinner via GSAP registry ─────────────────────────────────────
  //
  // Must run AFTER mount so GSAP can resolve the element in the DOM.

  spinnerHandle = gsapRegistry.create('reconnecting:spinner');
  spinnerHandle.timeline.to(spinner, { rotation: 360, duration: 1, repeat: -1, ease: 'linear' });

  // ── ESC: intentionally no listener (spec: ESC is no-op on reconnecting) ────

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    if (spinnerHandle !== null) {
      spinnerHandle.dispose();
      spinnerHandle = null;
    }

    root.remove();
  }

  return { element: root, dispose };
}
